<template>
  <v-container class="page-earlypay2">
    <div class="bg"></div>
    <DateSlider
      showFormat="M월 DD일 (ddd)"
      class="width-100 mb-5 mt-5"
      :date="date"
      :max="today"
      @update:date="$router.replace({ query: { date: $event } })"
      @onClickDate="
        $router.push({
          path: '/earlypay/statistics/month-sales',
          query: { date },
        })
      "
      style="margin-bottom: 30px;"
    />
    <DataFetch :date="date">
      <template slot-scope="{ data, rawData, isLoading }">
        <div v-if="!isLoading && data">
          <CommonSection>
            <template v-slot:content>
              <SalesTotal :data="data" />
            </template>
          </CommonSection>

          <CommonSection>
            <template v-slot:content>
              <FeeTotal :data="data" />
            </template>
          </CommonSection>

          <CommonSection>
            <template v-slot:content>
              <PurchaseSchedule :data="data.purchaseSchedule" />
            </template>
          </CommonSection>

          <LoanButtons class="mb-5" />

          <BasicButton v-if="!isEarlyPaymentEnabled" class="basic-button--apply" @click="moveToEarlypay">
              카드배달 365 오전 선정산
          </BasicButton>

          <!-- 매출 통계 차트 추가 -->
          <CommonSection v-if="false">
            <template v-slot:content>
              <SalesChart 
                :data="data"
                :raw-data="rawData"
                @error="(e) => console.error('Chart error:', e)"
              />
            </template>
          </CommonSection>
        </div>
        <div v-else>
            <v-card class="no-data-card">
              <v-card-text class="text-center">
                <v-icon large color="grey lighten-1" class="mb-4">mdi-alert-circle-outline</v-icon>
                <div class="title grey--text text--darken-1">매출 데이터가 없습니다</div>
                <div class="subtitle-1 grey--text mt-2">
                  선택하신 날짜의 매출 정보를 찾을 수 없습니다
                </div>
              </v-card-text>
            </v-card>
        </div>
      </template>
    </DataFetch>

  </v-container>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions, mapGetters } from 'vuex'
import { defaultApi } from '@/plugins/api/defaultApi'
import { commaFormat } from '@/filter/number'

const today = moment().format('YYYY-MM-DD')

/**
 * 정산 페이지는 미래 날짜 조회 불가하므로
 * 쿼리스트링으로 받은 date 날짜 검사
 */
function queryNavigationGuard(to, from, next) {
  const date = to.query.date
  if (!date) {
    // 쿼리에 날짜가 없으면 route객체에 오늘 날짜 정보만 넣어서 보내줌
    to.query.date = moment().subtract(1, 'days').format('YYYY-MM-DD')
    return next()
  } else if (isNaN(Date.parse(date)) || moment(date).isAfter(today)) {
    // 날짜 형식이 아니거나 오늘 이후인 경우 오늘 날짜 페이지로 이동
    return next({ path: `${to.path}?date=${today}` })
  }

  next()
}

export default {
  components: {
    DateSlider: () => import('@/components/earlypay/template/DateSlider.vue'),
    DataFetch: () => import('@/components/sales-info/DataFetch.vue'),
    CommonSection: () => import('@/components/earlypay/template/CommonSection.vue'),
    SalesTotal: () => import('@/components/sales-info/SalesTotal.vue'),
    FeeTotal: () => import('@/components/sales-info/FeeTotal.vue'),
    PurchaseSchedule: () => import('@/components/sales-info/PurchaseSchedule.vue'),
    LoanButtons: () => import('@/components/loan/LoanButtons.vue'),
    SalesChart: () => import('@/components/sales-info/SalesChart.vue'),
  },
  data() {
    return {
      date: this.$route.query.date,
      today,
      offer: null,
      showCardDeliveryDeposits: true,
      investingAmount: 0,
    }
  },
  beforeRouteEnter: queryNavigationGuard,
  beforeRouteUpdate: queryNavigationGuard,
  computed: {
    ...mapState('contract', {
      contractCurrent: 'current',
    }),
    ...mapState('auth', ['me']),
    ...mapState('myfinbiz', ['organization']),
    ...mapState('env', ['mode']),
    ...mapGetters('loan', [
      'isPullRevenueOfferRequest',
      'investingPullRevenue',
      'executedPullRevenue',
    ]),
    investingPercent() {
      if (!this.investingPullRevenue || !this.investingAmount) return 0
      return ((this.investingAmount / this.investingPullRevenue.amount) * 100).toFixed(0)
    },
    isExecuting() {
      return this.investingPullRevenue?.status === 'executing'
    },
    isContractable() {
      if (
        !this.investingPullRevenue ||
        !['investing', 'invested'].includes(this.investingPullRevenue.status) ||
        this.investingAmount < 1
      ) {
        return false
      }

      const now = this.$moment()
      const start = this.$moment(this.investingPullRevenue.contractStartTime)
      const end = this.$moment(this.investingPullRevenue.contractEndTime)
      return now.isSameOrAfter(start) && now.isBefore(end)
    },
    isEarlyPaymentEnabled() {
      console.log('*********SALES PAGE isEarlyPaymentEnabled', this.contractCurrent)
      return (
        this.contractCurrent?.status === 'started' &&
        this.contractCurrent.isEarlyPaymentEnabled === true
      )
    },
  },
  methods: {
    ...mapActions('auth', ['getMe', 'updateMe']),
    ...mapActions('evaluation', ['isKcsAgreements', 'isHometaxAccounts']),
    commaFormat,
    handleShouldShow(value) {
      this.showCardDeliveryDeposits = value;
    },
    async moveToLoan() {
      if (!(await this.isHometaxAccounts())) {
        this.$router.push({
          path: '/auth/accounts/hometax',
          query: { next: '/loan/pull-revenue' },
        })
      } else if (!(await this.isKcsAgreements())) {
        this.$router.push({
          path: '/signup/agreements/kcs',
          query: { next: '/loan/pull-revenue' },
        })
      } else {
        this.$router.push('/loan/pull-revenue')
      }
    },
    async moveToEarlypay() {
      await this.$router.push('/earlypay-intro')
    }
  },
  watch: {
    contractCurrent: {
      deep: true,
      immediate: true,
      async handler(v) {
        if (!v) {
          return
        }
        // // 선정산이 꺼져있으면 강제로 메인으로 이동시킨다
        // if (v.isEarlyPaymentEnabled === false) {
        //   await this.$router.replace('/')
        // }
      },
    },
    investingPullRevenue: {
      immediate: true,
      async handler(v) {
        if (!v) return
        const { data } = await defaultApi.getInvestingAmount(v.uuid)
        this.investingAmount = data || 0
      },
    },
  },
  async created() {
    await this.updateMe();
    console.log('me:', this.me);
    console.log('organization:', this.organization);
  },
}
</script>

<style lang="scss" scoped>
.page-earlypay2 {
  :deep .section-container section {
    background-color: white;

    .layout {
      margin-bottom: 10px;
      font-weight: bold;
    }

    .layout.subtitle {
      font-size: 13px;
    }

    .layout.subtitle::before {
      content: '└';
    }

    p:last-child {
      margin-top: 30px;
      margin-bottom: 0;
      font-size: 12px;
      text-align: left;
      color: #666;
    }

    img.icon {
      width: 17px;
      margin-right: 5px;
    }
  }

  @media screen and (max-width: 319px) {
    padding: 12px 0;
  }
}

.basic-button--apply {
  background-color: #4163d5 !important;
  color: #ffe15b !important;
  border-radius: 20px;
}

.no-data-card {
  margin: 40px 0;
  padding: 40px;
  background-color: #ffffff !important;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;

  .v-icon {
    font-size: 48px;
  }

  .title {
    font-size: 20px;
    font-weight: 600;
  }

  .subtitle-1 {
    font-size: 14px;
    opacity: 0.8;
  }
}
</style>
